import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import { ButtonUI, Checkbox, LoadingSkeleton, TaptapList, TextWithTooltip } from 'sonata-react-components';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useListGrouping } from '../../../grouping/hooks/useListGrouping';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { getCountryLabel } from '../../../MultiFlightMap.helpers';
import { centerMap, sanitizeObj } from '../../MapView/map.helpers';
import { AttributionAndGroup } from '../common/AttributionAndGroup';
import { Title } from '../common/Title';
import ListsHeader from '../ListsHeader/ListsHeader';
import { getLocationListTitle } from './locationList.helpers';

import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { GroupView } from '../../../grouping/domain/grouping.types';

import '../lists.scss';

import { useModals } from '@/router';
import { type ActivationAreasAction, ActivationAreasModal } from '../../ActivationAreasModal/ActivationAreasModal';

type Props = {
  mapId: string;
};

type State = {
  searchText: string[];
  showActivationAreasModal: boolean;
  titlesById: Record<string, string>;
};

type TaptapListItem = {
  id: string;
  groupIds: string[];
  groups: GroupView[];
  subtitle: string;
  title: string;
  showSmart: boolean;
  notEditable: boolean;
  status: 'default' | 'highlighted' | 'notVisible';
  shape: Partial<Zone>;
};

function LocationList({ mapId }: Props) {
  const { formatMessage } = useIntl();
  const { [mapId]: map } = useMap();
  const modals = useModals();
  const {
    fullShapes,
    loadingLocationList,
    selectedItemId,
    selectedItems: { locations: selectedLocationsIds },
    setEditedPolygonId,
    setMode,
    setSelectedItemId,
    setSelectedItems,
    temporaryLocation,
    updateZone,
    user,
    userRoles,
    zoneGroups
  } = useMultiFlightMap();
  const locationList = useMemo(
    () =>
      temporaryLocation?.name
        ? fullShapes.filter(shape => shape.cartographyLevelId !== temporaryLocation.name && !shape.deleted)
        : fullShapes.filter(shape => !shape.deleted),
    [fullShapes, temporaryLocation]
  );

  const smartGroupPermission = checkUserResourcePermissions('smartGroups', user, userRoles);
  const {
    clearGroupFilter,
    deleteItems,
    editGroup,
    groupFilter,
    groupsOf,
    hasActiveGroupFilter,
    isHighlighted,
    matchesGroupFilter
  } = useListGrouping('locations');

  const [{ searchText, showActivationAreasModal, titlesById }, setState] = useState<State>({
    searchText: [],
    showActivationAreasModal: false,
    titlesById: {}
  });

  const handleSearchChange = (text: string) => {
    const searchValues = text.length ? text.split(',').map((a: string) => a.trim()) : [];
    setState(prevState => ({
      ...prevState,
      searchText: searchValues
    }));
  };

  const handleSwitchChange = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const zoneToUpdate = locationList.find(shape => shape._id === id);
    if (zoneToUpdate) {
      updateZone({
        ...zoneToUpdate,
        visible: !zoneToUpdate.visible
      });
    }
  };

  const handleDeleteChange = (id: string) => {
    deleteItems([id]);
  };

  const handleDeleteAllChange = () => {
    deleteItems(selectedLocationsIds);
  };

  const handleCenterMapOnPoint = useCallback(
    (shape: Zone) => {
      setSelectedItemId(shape._id?.toString() || null);
      if (shape.type === 'polygon') {
        setEditedPolygonId(shape._id as string);
      }
      centerMap(shape, map);
    },
    [map, setEditedPolygonId, setSelectedItemId]
  );

  const handleTitleChange = (value: string, id?: string) => {
    if (!id) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      titlesById: {
        ...prevState.titlesById,
        [id]: value
      }
    }));
    const zoneToUpdate = locationList.find(shape => shape._id === id);
    if (zoneToUpdate) {
      const sanitizedZone = sanitizeObj(zoneToUpdate);
      updateZone({
        ...sanitizedZone,
        name: value
      });
    }
  };

  const handleSelectionChange = useCallback(
    (selectedRows: { id: string }[]) => {
      setSelectedItems(
        'locations',
        selectedRows.map(row => row.id)
      );
    },
    [setSelectedItems]
  );

  const hiddenSmartGroupIds = useMemo(
    () => new Set(smartGroupPermission ? [] : zoneGroups.filter(group => group.smart).map(group => String(group._id))),
    [smartGroupPermission, zoneGroups]
  );

  const capitalizeFirstLetter = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

  const getZoneTypeLabel = (zone: Partial<Zone>): string => {
    if (zone.country && zone.cartographyLevel && zone.cartographyLevelId) {
      return formatMessage({
        defaultMessage: zone.cartographyLevel,
        id: `adminBoundaries.boundaryType.${normalizeCountryToCca3(zone.country).toUpperCase()}_${zone.cartographyLevel}`
      });
    }
    return capitalizeFirstLetter(zone.type || '');
  };

  const getRowStatus = (shape: Partial<Zone>): TaptapListItem['status'] => {
    if (!shape.visible) {
      return 'notVisible';
    }
    const id = shape._id?.toString() ?? '';
    return selectedItemId === id || isHighlighted(id) ? 'highlighted' : 'default';
  };

  const data: TaptapListItem[] = useMemo(() => {
    if (locationList.length === 0) {
      return [];
    }
    return locationList.map(shape => ({
      groupIds: shape?.groups?.map(String) ?? [],
      groups: groupsOf(shape?.groups).filter(group => !hiddenSmartGroupIds.has(group.id)),
      id: shape._id as string,
      notEditable: Boolean(shape.cartographyLevelId),
      shape,
      showSmart: Boolean(shape.smart),
      status: getRowStatus(shape),
      subtitle: [
        getZoneTypeLabel(shape),
        ...(['circle', 'torus'].includes(shape.type || '') ? [`${normalize((shape.radius || 0) / 1000)} km`] : []),
        getCountryLabel(shape.country || '')
      ].join(' | '),
      title: getLocationListTitle(shape)
    }));
  }, [locationList, groupsOf, hiddenSmartGroupIds, selectedItemId, isHighlighted]);

  const formattedData: TaptapListItem[] = useMemo(() => {
    const dataWithEditedName = data.map(item => ({
      ...item,
      title: titlesById[item.id] || item.title
    }));

    const filteredBySearch = (() => {
      if (searchText.length === 0) {
        return dataWithEditedName;
      }
      const lowercaseText = searchText.map(s => s.toLowerCase());
      return dataWithEditedName.filter(({ groups, id, subtitle, title }) => {
        const groupNames = groups.map(group => group.name.toLowerCase());
        return lowercaseText.some(search =>
          [title, id, subtitle, ...groupNames].join('~').toLowerCase().includes(search)
        );
      });
    })();
    return filteredBySearch.filter(item => matchesGroupFilter(item.groupIds));
  }, [data, titlesById, searchText, matchesGroupFilter]);

  const defaultSelectedItems = useMemo(
    () => formattedData.filter(item => selectedLocationsIds.includes(item.id)),
    [formattedData, selectedLocationsIds]
  );

  const handleActivationAreaModalClose = () => {
    setState(prevState => ({ ...prevState, showActivationAreasModal: false }));
  };

  const handleActivationAreasModalSave = (selectedAction: ActivationAreasAction) => {
    setState(prevState => ({ ...prevState, showActivationAreasModal: false }));
    if (['circularArea', 'polygonArea'].some(action => action === selectedAction)) {
      setMode(selectedAction === 'circularArea' ? 'add-circles' : 'draw-polygons');
    } else if (selectedAction === 'cartographySelector') {
      modals.open('/campaigns/new/cartographyModal', {
        state: { modalId: 'cartography-modal' }
      });
    } else if (selectedAction === 'areasFromPOIs') {
      modals.open('/campaigns/new/poiFinderVenuesModal', {
        state: { modalId: 'poi-finder-venues-modal' }
      });
    } else {
      modals.open('/campaigns/new/uploadLocationsModal', {
        state: { modalId: 'upload-file-modal' }
      });
    }
  };

  if (loadingLocationList) {
    return <LoadingSkeleton count={10} height={62} />;
  }

  return (
    <div className="mapList">
      <ListsHeader
        actionButtonsId={`actionButtons-locationsList-${mapId}`}
        activeFiltersCount={hasActiveGroupFilter ? 1 : 0}
        filters={[groupFilter]}
        filtersId={`filters-locationsList-${mapId}`}
        onClearFilters={clearGroupFilter}
        search={{
          id: `locationsList-textFilter-${mapId}`,
          name: `locationsList-textFilter-${mapId}`,
          onChange: handleSearchChange,
          placeholder: formatMessage({
            defaultMessage: 'Search by name, ID...',
            id: 'multiFlightListsMap.search.locations'
          })
        }}
      />
      <div className="mapList__listContainer">
        <TaptapList
          className="mapList__list"
          data={formattedData}
          defaultSelectedItems={defaultSelectedItems}
          id={`locationsList-${mapId}`}
          onSelectionChange={items => handleSelectionChange(items as { id: string }[])}
          selectionType="rowClick"
        >
          {locationList.length > 0 && (
            <TaptapList.Toolbar>
              <TaptapList.Toolbar.SelectionSummary />
              <TaptapList.Toolbar.SelectAll />
              <div className="toolbarDeleteAll">
                <ButtonUI
                  disabled={selectedLocationsIds.length === 0}
                  icon="fa-regular fa-trash"
                  id={`deleteSelectedAction-${mapId}`}
                  onClick={handleDeleteAllChange}
                  shape="link"
                  size="ultraSmall"
                  text={formatMessage({
                    defaultMessage: 'Delete',
                    id: 'multiFlightListsMap.emptyState.locations.delete'
                  })}
                  type="secondary"
                />
              </div>
            </TaptapList.Toolbar>
          )}
          <TaptapList.RowsListVirtualized<TaptapListItem>
            emptyStateDescription={formatMessage(
              locationList.length
                ? {
                    defaultMessage: 'There are no activation areas added',
                    id: 'multiFlightListsMap.emptyState.locations.description'
                  }
                : {
                    defaultMessage: 'Add your first Activation Area clicking below.',
                    id: 'multiFlightListsMap.emptyState.locations.getStarted'
                  }
            )}
            emptyStateIcon="fa-solid fa-empty-set"
            emptyStateTitle={formatMessage(
              locationList.length
                ? { defaultMessage: 'No activation areas', id: 'multiFlightListsMap.emptyState.locations.title' }
                : { defaultMessage: 'Ready to get started?', id: 'multiFlightListsMap.emptyState.readyToStart' }
            )}
            focusItemId={selectedItemId}
          >
            {listItem =>
              listItem.map(item => {
                return (
                  <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                    <Title
                      className="mapList__listRowsListRowItemTitle"
                      notEditable={item.notEditable}
                      onTextChange={(newValue: string) => handleTitleChange(newValue, item.id)}
                      rowId={item.id}
                      showSmart={item.showSmart}
                      text={item.title}
                    />
                    <div className="mapList__listRowsListRowItemActions">
                      <Checkbox
                        active={item.status !== 'notVisible'}
                        classes="mapList__listRowsListRowVisibleSwitch"
                        id={`mapList-switch-locations-${mapId}-${item.id}`}
                        onClickAction={(_id, _checked, event) => handleSwitchChange(item.id, event)}
                        squareColor="#21CDCD"
                        tiny
                        type="switch"
                      />
                      <ButtonUI
                        className="mapList__listRowsListRowDeleteButton"
                        icon="fa solid fa-xmark"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleDeleteChange(item.id);
                        }}
                        shape="link"
                        size="ultraSmall"
                        type="secondary"
                      />
                    </div>
                    <div className="mapList__listRowsListRowItemSubtitle">
                      <ButtonUI
                        className="mapList__listRowsListRowPinButton"
                        icon="fa-solid fa-map-pin"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleCenterMapOnPoint(item.shape as Zone);
                        }}
                        shape="link"
                        size="ultraSmall"
                        type="secondary"
                      />
                      <TextWithTooltip text={item.subtitle} />
                    </div>
                    <AttributionAndGroup
                      groupMenu={{ family: 'locations', groups: item.groups, onEditGroup: editGroup }}
                    />
                  </TaptapList.RowsListVirtualized.Row>
                );
              })
            }
          </TaptapList.RowsListVirtualized>
        </TaptapList>
      </div>
      <div className="mapList__addActivationAreasContainer">
        <ButtonUI
          className="mapList__addPOIButton"
          icon="fa-solid fa-plus"
          onClick={() => setState(prevState => ({ ...prevState, showActivationAreasModal: true }))}
          text={formatMessage({
            defaultMessage: 'Add Activation Areas',
            id: 'activationAreas.addButton'
          })}
          type="secondary"
        />
      </div>
      {showActivationAreasModal && (
        <ActivationAreasModal
          isModalOpened
          onModalClose={handleActivationAreaModalClose}
          onModalSave={handleActivationAreasModalSave}
        />
      )}
    </div>
  );
}
export default LocationList;
